"use client";

import { useState } from "react";
import Link from "next/link";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, RotateCcw, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/components/providers/auth-provider";
import { formatNumber } from "@/lib/format-datetime";
import type { Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { QuizRichText } from "@/features/lessons/components/QuizRichText";
import {
  fetchLearnTestAttempts,
  startLearnTest,
  submitLearnTest,
  type LearnTestResult,
  type LearnTestStart,
  type LearnTestSummary,
} from "./api";
import { smoothScrollBehavior } from "@/lib/scroll-behavior";

/**
 * A module test in the reader (`LN-RDR02`, docs/35 §5 item 3).
 *
 * Until 2026-09-23 a test lesson opened as an empty page: the reader had no
 * view for a `quiz` lesson and the course engine's start route asked for an
 * enrolment a Learn reader does not hold. The test is the course engine's own
 * (`QuizAttempt`, graded by `quizService.submitAttempt`), reached through the
 * Learn routes, which open it by the Learn access decision.
 *
 * Three states: the start card (how many questions, the pass mark, the retake
 * rule, what it covers, the reader's best so far), the questions on one page
 * with a running count, and the result, which marks every answer and shows
 * each explanation. A pass writes the lesson's completion on the server, so the
 * track's progress moves without a second click.
 */
type Phase =
  | { tag: "start" }
  | { tag: "taking"; test: LearnTestStart; answers: Record<string, string[]>; submitting: boolean }
  | { tag: "result"; test: LearnTestStart; answers: Record<string, string[]>; result: LearnTestResult };

export function LearnModuleTest({
  trackSlug,
  moduleKey,
  lessonKey,
  summary,
  covers,
  locale,
  signInHref,
  nextHref,
}: {
  trackSlug: string;
  moduleKey: string;
  lessonKey: string;
  summary: LearnTestSummary;
  /** The module's reading lessons, the ones the questions are drawn from. */
  covers: string[];
  locale: Locale;
  signInHref: string;
  nextHref: string | null;
}) {
  const t = useTranslations("learn.reader");
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>({ tag: "start" });
  const [error, setError] = useState(false);
  const ref = { trackSlug, moduleKey, lessonKey };
  const n = (value: number) => formatNumber(value, locale);
  const pick = (en: string, bn?: string) => (locale === "bn" && bn ? bn : en);

  const attemptsKey = ["learn", "test", trackSlug, moduleKey, lessonKey, "attempts"];
  const { data: attempts = [] } = useQuery({
    queryKey: attemptsKey,
    queryFn: () => fetchLearnTestAttempts(ref),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
  const best = attempts.reduce<number | null>(
    (top, attempt) => (top === null || attempt.percentage > top ? attempt.percentage : top),
    null,
  );
  const retakeClosed =
    (!summary.allowRetry && attempts.length > 0) ||
    (summary.maxAttempts !== null && attempts.length >= summary.maxAttempts);

  async function begin(): Promise<void> {
    setError(false);
    try {
      const test = await startLearnTest(ref);
      setPhase({ tag: "taking", test, answers: {}, submitting: false });
    } catch {
      setError(true);
    }
  }

  function choose(questionId: string, optionId: string, multiple: boolean): void {
    setPhase((current) => {
      if (current.tag !== "taking") return current;
      const had = current.answers[questionId] ?? [];
      const next = multiple
        ? had.includes(optionId)
          ? had.filter((id) => id !== optionId)
          : [...had, optionId]
        : [optionId];
      return { ...current, answers: { ...current.answers, [questionId]: next } };
    });
  }

  async function submit(): Promise<void> {
    if (phase.tag !== "taking") return;
    setError(false);
    setPhase({ ...phase, submitting: true });
    try {
      const result = await submitLearnTest({
        ...ref,
        attemptId: phase.test.attemptId,
        answers: phase.test.questions.map((question) => ({
          questionId: question._id,
          selectedOptionIds: phase.answers[question._id] ?? [],
        })),
      });
      setPhase({ tag: "result", test: phase.test, answers: phase.answers, result });
      void queryClient.invalidateQueries({ queryKey: attemptsKey });
      if (result.completed) {
        void queryClient.invalidateQueries({ queryKey: ["learn", "track", trackSlug, "viewer"] });
        void queryClient.invalidateQueries({ queryKey: ["learn", "tracks", "viewer"] });
        void queryClient.invalidateQueries({
          queryKey: ["learn", "lesson", trackSlug, moduleKey, lessonKey, "viewer"],
        });
      }
      window.scrollTo({ top: 0, behavior: smoothScrollBehavior() });
    } catch {
      setError(true);
      setPhase({ ...phase, submitting: false });
    }
  }

  const errorLine = error ? (
    <p className="mt-4 text-sm font-medium text-red-700" role="alert">
      {t("testError")}
    </p>
  ) : null;

  if (phase.tag === "start") {
    const stats = [
      { value: n(summary.questionCount), label: t("testQuestionsLabel") },
      { value: `${n(summary.passPercentage)}%`, label: t("testPassLabel") },
      {
        value: summary.allowRetry ? t("testRetakeAny") : t("testRetakeOnce"),
        label: summary.allowRetry ? t("testRetakeLabel") : t("testRetakeOnceLabel"),
      },
    ];
    return (
      <div>
        <p className="text-base leading-relaxed text-neutral-700 lg:text-[17px]">{t("testIntro")}</p>
        <dl className="mt-6 grid gap-3 sm:grid-cols-3">
          {stats.map((stat) => (
            <Card className="flex flex-col-reverse p-4 shadow-none" key={stat.label}>
              <dt className="mt-0.5 text-[13px] text-neutral-600">{stat.label}</dt>
              <dd className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[rgb(var(--foreground))]">
                {stat.value}
              </dd>
            </Card>
          ))}
        </dl>
        {covers.length > 0 ? (
          <Card className="mt-5 px-5 pb-2 pt-4 shadow-none">
            <p className="font-[family-name:var(--font-heading)] text-sm font-bold text-[rgb(var(--foreground))]">
              {t("testCovers")}
            </p>
            <ol className="mt-1">
              {covers.map((title, index) => (
                <li
                  className="flex gap-3 border-t border-neutral-100 py-2.5 text-[15px] text-neutral-700 first:border-t-0"
                  key={title}
                >
                  <span className="pt-0.5 font-mono text-xs text-neutral-500">{n(index + 1)}</span>
                  {title}
                </li>
              ))}
            </ol>
          </Card>
        ) : null}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
          {isAuthenticated ? (
            <Button
              className="h-12 px-6 text-base font-bold"
              disabled={retakeClosed}
              onClick={() => void begin()}
              type="button"
            >
              {attempts.length > 0 ? t("testRetry") : t("testStart")}
              <ArrowRight aria-hidden className="h-4 w-4" />
            </Button>
          ) : (
            <Button asChild className="h-12 px-6 text-base font-bold">
              <Link href={signInHref}>{t("testSignIn")}</Link>
            </Button>
          )}
          {isAuthenticated ? (
            <span className="text-sm text-neutral-600">
              {retakeClosed
                ? t("testNoRetry")
                : best === null
                  ? t("testNotTaken")
                  : t("testBest", { percent: n(best) })}
            </span>
          ) : null}
        </div>
        {errorLine}
      </div>
    );
  }

  const { test, answers } = phase;
  const answered = test.questions.filter((q) => (answers[q._id] ?? []).length > 0).length;
  const review = phase.tag === "result" ? phase.result : null;
  const byQuestion = new Map((review?.answers ?? []).map((row) => [row.questionId, row]));

  return (
    <div>
      {review ? (
        <Card
          className={cn(
            "mb-8 p-5 shadow-none sm:p-6",
            review.passed ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50",
          )}
        >
          <p
            className={cn(
              "font-[family-name:var(--font-heading)] text-xl font-bold",
              review.passed ? "text-emerald-800" : "text-amber-900",
            )}
          >
            {review.passed
              ? t("testPassed", { percent: n(review.percentage) })
              : t("testFailed", { percent: n(review.percentage), pass: n(summary.passPercentage) })}
          </p>
          <p className="mt-1.5 text-sm text-neutral-700">
            {review.passed ? t("testPassedBody") : t("testFailedBody")}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {review.passed && nextHref ? (
              <Button asChild className="h-11">
                <Link href={nextHref}>
                  {t("nextLesson")}
                  <ArrowRight aria-hidden className="h-4 w-4" />
                </Link>
              </Button>
            ) : null}
            {summary.allowRetry ? (
              <Button
                className="h-11 bg-white"
                onClick={() => {
                  setPhase({ tag: "start" });
                  window.scrollTo({ top: 0, behavior: smoothScrollBehavior() });
                }}
                type="button"
                variant="secondary"
              >
                <RotateCcw aria-hidden className="h-4 w-4" />
                {t("testRetry")}
              </Button>
            ) : null}
          </div>
        </Card>
      ) : null}

      <ol className="space-y-8">
        {test.questions.map((question, index) => {
          const chosen = answers[question._id] ?? [];
          const graded = byQuestion.get(question._id);
          const multiple = question.type === "multiple_choice";
          return (
            <li key={question._id}>
              <div className="flex gap-2.5">
                <span className="mt-px font-[family-name:var(--font-heading)] text-[15px] font-bold text-primary-ink">
                  {n(index + 1)}
                </span>
                <QuizRichText
                  className="min-w-0 flex-1 text-[15px] font-semibold text-[rgb(var(--foreground))] [&_p]:!m-0"
                  html={pick(question.questionText_en, question.questionText_bn)}
                />
              </div>
              <ul className="mt-3 space-y-2">
                {question.options.map((option, optionIndex) => {
                  const isChosen = chosen.includes(option._id);
                  // With `showCorrectAnswers` off the grader sends no key, and the
                  // reader still learns whether each of their own picks was right.
                  const isRight = graded?.correctOptionIds
                    ? graded.correctOptionIds.includes(option._id)
                    : isChosen && graded?.isCorrect === true;
                  const state = graded
                    ? isRight
                      ? "right"
                      : isChosen
                        ? "wrong"
                        : null
                    : isChosen
                      ? "chosen"
                      : null;
                  return (
                    <li key={option._id}>
                      <Button
                        aria-pressed={isChosen}
                        className={cn(
                          "h-auto w-full justify-start gap-3 whitespace-normal border px-3.5 py-2.5 text-left text-sm font-normal text-[rgb(var(--foreground))]",
                          state === "right"
                            ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-50"
                            : state === "wrong"
                              ? "border-red-300 bg-red-50 hover:bg-red-50"
                              : state === "chosen"
                                ? "border-primary/50 bg-primary/5 hover:bg-primary/5"
                                : "border-[rgb(var(--border))] bg-[rgb(var(--surface))]",
                        )}
                        disabled={Boolean(review) || phase.tag !== "taking" || phase.submitting}
                        onClick={() => choose(question._id, option._id, multiple)}
                        type="button"
                        variant="tertiary"
                      >
                        <span
                          aria-hidden
                          className={cn(
                            "flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-[family-name:var(--font-heading)] text-xs font-bold",
                            state === "right"
                              ? "bg-emerald-500 text-white"
                              : state === "wrong"
                                ? "bg-red-500 text-white"
                                : state === "chosen"
                                  ? "bg-primary text-white"
                                  : "bg-neutral-100 text-neutral-600",
                          )}
                        >
                          {"ABCDEFGH"[optionIndex] ?? optionIndex + 1}
                        </span>
                        <QuizRichText
                          as="span"
                          className="min-w-0 flex-1 [&_p]:!m-0"
                          html={pick(option.text_en, option.text_bn)}
                        />
                        {state === "right" ? (
                          <Check aria-hidden className="ml-auto h-4 w-4 shrink-0 text-emerald-700" strokeWidth={3} />
                        ) : state === "wrong" ? (
                          <X aria-hidden className="ml-auto h-4 w-4 shrink-0 text-red-700" strokeWidth={2.6} />
                        ) : null}
                      </Button>
                    </li>
                  );
                })}
              </ul>
              {graded && (graded.explanation_en || graded.explanation_bn) ? (
                <div className="mt-2.5 rounded-[var(--radius)] border border-neutral-200 bg-neutral-50 px-3.5 py-2.5 text-sm text-neutral-700">
                  <QuizRichText
                    className="[&_p]:!m-0"
                    html={pick(graded.explanation_en ?? "", graded.explanation_bn)}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>

      {phase.tag === "taking" ? (
        <div className="sticky bottom-0 z-10 mt-8 flex items-center justify-between gap-4 border-t border-[rgb(var(--border))] bg-[rgb(var(--background))] py-4">
          <span className="text-sm font-medium text-neutral-600">
            {t("testAnswered", { done: n(answered), total: n(test.questions.length) })}
          </span>
          <Button
            className="h-11 px-5 font-bold"
            disabled={phase.submitting}
            onClick={() => void submit()}
            type="button"
          >
            {phase.submitting ? t("testSubmitting") : t("testSubmit")}
          </Button>
        </div>
      ) : null}
      {errorLine}
    </div>
  );
}
